import { apiClient } from './client';
import { Organization, OrganizationStats, FeatureFlags, OrganizationLimits } from '../../types/api';

export const organizationsApi = {
  /**
   * Get current organization details
   * @returns Current organization
   */
  getCurrentOrganization: async (): Promise<Organization> => {
    const response = await apiClient.get<Organization>('/organizations/current');
    return response.data;
  },

  /**
   * Update current organization
   * @param data - Organization update data
   * @returns Updated organization
   */
  updateCurrentOrganization: async (data: Partial<Organization>): Promise<Organization> => {
    const response = await apiClient.put<Organization>('/organizations/current', data);
    return response.data;
  },

  /**
   * Get organization statistics
   * @returns Organization statistics
   */
  getOrganizationStats: async (): Promise<OrganizationStats> => {
    const response = await apiClient.get<OrganizationStats>('/organizations/current/stats');
    return response.data;
  },

  /**
   * Get organization features
   * @returns Feature flags
   */
  getOrganizationFeatures: async (): Promise<FeatureFlags> => {
    const response = await apiClient.get<FeatureFlags>('/organizations/current/features');
    return response.data;
  },

  /**
   * Get organization limits
   * @returns Organization limits
   */
  getOrganizationLimits: async (): Promise<OrganizationLimits> => {
    const response = await apiClient.get<OrganizationLimits>('/organizations/current/limits');
    return response.data;
  },

  /**
   * Get all organizations (super admin only)
   * @returns List of all organizations
   */
  getAllOrganizations: async (): Promise<Organization[]> => {
    const response = await apiClient.get<Organization[]>('/organizations/');
    return response.data;
  },

  /**
   * Create a new organization (super admin only)
   */
  createOrganization: async (data: OrganizationCreateData): Promise<Organization> => {
    const response = await apiClient.post<Organization>('/organizations/', data);
    return response.data;
  },

  /**
   * Provision tenant schema for an organization
   */
  provisionSchema: async (organizationId: string): Promise<ProvisionResponse> => {
    const response = await apiClient.post<ProvisionResponse>(`/tenants/provision/${organizationId}`);
    return response.data;
  },

  /**
   * Check schema provisioning status
   */
  getSchemaStatus: async (organizationId: string): Promise<SchemaStatus> => {
    const response = await apiClient.get<SchemaStatus>(`/tenants/schemas/${organizationId}/status`);
    return response.data;
  },
};

export interface OrganizationCreateData {
  name: string;
  code: string;
  type?: string;
  email?: string;
  phone?: string;
  address?: string;
  is_active?: boolean;
}

export interface ProvisionResponse {
  message: string;
  schema_name: string;
  organization: Organization;
}

export interface SchemaStatus {
  organization_id: string;
  organization_code: string;
  schema_name: string;
  provisioned: boolean;
  table_count: number;
  status: string;
}
